import { dataLocalStorage, getLocalStorage } from "src/helper/publicFunction";
import { styleIssues } from "src/pages/catalogue/style";
import UrlApi from "src/utils/Url";

// The public website that the content on these pages feeds: dev for the dev
// API, the real site otherwise.
export const WEBSITE = /api-dev\./.test(UrlApi.baseUrl || "") || !UrlApi.baseUrl ? "https://dev.safrati.ai" : "https://safrati.ai";

export const PAGES = {
  home: { name: "Home page", path: "/" },
  about: { name: "About page", path: "/aboutUs" },
  ai: { name: "Safrati AI page", path: "/safratiAI" },
  // The website calls it "Questions" (menu and footer), headed "What people ask".
  faqs: { name: "Questions page", path: "/faqs" },
  contact: { name: "Contact page", path: "/contactUs" },
  terms: { name: "Terms page", path: "/termsAndConditions" },
  privacy: { name: "Privacy page", path: "/privacyPolicy" },
  refund: { name: "Refund policy page", path: "/refundPolicy" },
};

export const pageUrl = (key) => `${WEBSITE}${PAGES[key].path}`;

// An uploaded file's public address, or "" for none.
export function imageUrl(path) {
  if (!path || typeof path !== "string") return "";
  if (/^(https?:|blob:|data:)/.test(path)) return path;
  return `${UrlApi.baseUrlImage || ""}${path}`;
}

export const isFile = (value) => typeof File !== "undefined" && value instanceof File;

// ---------------------------------------------------------------------------
// Placeholder text: the website must never show filler or invented words.
// Each check returns plain sentences an editor can act on; none of them
// blocks saving, they ask a person to look again.

const LOREM = /\blorem ipsum\b|\bdolor sit amet\b|\bconsectetur adipiscing\b|\bcontent here, content here\b/i;
const TEST_WORDS = /^(test|testing|asdf|qwerty|xxx+|aaa+|sample|placeholder|dummy|todo|tbd)\b/i;
// The team's own test records, wherever the words appear.
const TEST_ANYWHERE = /zz test|qa test|test entry/i;
// Other visa companies' names, which crept in from copied text.
const COMPETITORS = /\b(atlys|visahq|ivisa|visa ?guide|boundless)\b/i;

// Words that look typed at random: letters and digits mixed ("jwjkqiojwpo3")
// or four consonants in a row that English rarely has ("jbdhebd").
function randomLookingWords(text) {
  const words =
    String(text)
      .toLowerCase()
      .match(/[a-z0-9]+/g) || [];
  return words.filter((word) => {
    if (word.length < 4) return false;
    if (/[a-z]/.test(word) && /\d/.test(word)) return true;
    const run = word.replace(/(sch|tch|ght|ngth|nst|str|thr|chr|phr|scr|spr|spl)/g, "a");
    return /[bcdfghjklmnpqrstvwxz]{4,}/.test(run);
  });
}

// The same sentence written twice or more.
function repeatsItself(text) {
  const sentences = String(text)
    .split(/[.!?]+\s+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 20);
  return new Set(sentences).size < sentences.length;
}

export function placeholderWarnings(...texts) {
  const text = texts.filter(Boolean).join(" \n ");
  if (!text.trim()) return [];
  const warnings = [];
  if (LOREM.test(text)) warnings.push("Has “lorem ipsum” filler text.");
  if (texts.some((t) => TEST_WORDS.test(String(t || "").trim())) || TEST_ANYWHERE.test(text)) warnings.push("Looks like a test entry.");
  const competitor = text.match(COMPETITORS);
  if (competitor) warnings.push(`Mentions “${competitor[0]}”, another company.`);
  if (randomLookingWords(text).length >= 2) warnings.push("Has words that look typed at random.");
  if (repeatsItself(text)) warnings.push("Repeats the same sentence.");
  return warnings;
}

// ---------------------------------------------------------------------------
// Colours as the website gets them ("#180d6f", "rgb(0, 224, 173)", "white").

const NAMED = { white: [255, 255, 255], black: [0, 0, 0] };

export function parseColor(value) {
  const text = String(value || "")
    .trim()
    .toLowerCase();
  if (NAMED[text]) return NAMED[text];
  let m = /^#([0-9a-f]{3})$/.exec(text);
  if (m) return m[1].split("").map((c) => parseInt(c + c, 16));
  m = /^#([0-9a-f]{6})$/.exec(text);
  if (m) return [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16));
  m = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/.exec(text);
  if (m) return [m[1], m[2], m[3]].map((n) => Math.min(Number(n), 255));
  return null;
}

export function toHex(value) {
  const rgb = parseColor(value);
  return rgb ? `#${rgb.map((n) => n.toString(16).padStart(2, "0")).join("")}` : "";
}

function luminance([r, g, b]) {
  const channel = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

// WCAG contrast ratio of two colours, or null when either is unreadable.
export function contrast(a, b) {
  const x = parseColor(a);
  const y = parseColor(b);
  if (!x || !y) return null;
  const [hi, lo] = [luminance(x), luminance(y)].sort((p, q) => q - p);
  return (hi + 0.05) / (lo + 0.05);
}

// ---------------------------------------------------------------------------

export function matches(item, search, fields) {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return fields.some((field) =>
    String(item[field] ?? "")
      .toLowerCase()
      .includes(q)
  );
}

export function dateText(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export const isOn = (value) => Number(value) === 1 || value === true;

export const validEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

// What the signed-in admin may do with one privilege group, as booleans.
export function rights(key) {
  const p = getLocalStorage(dataLocalStorage.privileges)?.[key] || {};
  const on = (v) => Number(v) === 1;
  return { read: on(p.access_read), write: on(p.access_write), edit: on(p.access_edit), delete: on(p.access_delete) };
}

/**
 * A house-style check's one-click fix (src/pages/catalogue/style.js), made in
 * a long text's own HTML (About and legal pages, hala-02): each run of text
 * between tags is fixed as the check fixes it, so bold, links, lists and
 * paragraphs stay as they are. `key` is the check ("us-spelling",
 * "apostrophe", …). The html comes back as it was when the fix can't be
 * made inside one run of text (a slip across two paragraphs): then there
 * is nothing to offer.
 */
export function fixInHtml(html, key) {
  const value = String(html ?? "");
  if (!value || typeof DOMParser === "undefined") return value;
  const body = new DOMParser().parseFromString(value, "text/html").body;
  const walker = body.ownerDocument.createTreeWalker(body, 4 /* NodeFilter.SHOW_TEXT */);
  let changed = false;
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    // The check trims what it fixes: the spaces around a run are kept.
    const [, before, words, after] = node.nodeValue.match(/^(\s*)([\s\S]*?)(\s*)$/);
    const fix = words ? styleIssues(words, { kind: "text" }).find((issue) => issue.key === key)?.fix : null;
    if (fix && fix !== words) {
      node.nodeValue = `${before}${fix}${after}`;
      changed = true;
    }
  }
  return changed ? body.innerHTML : value;
}

// ---------------------------------------------------------------------------
// What the public website really shows. These mirror safrati-web (branch dev)
// src/lib/publicCatalogue.js and the pages that use it; keep them in step.
// Each returns { shown, reason }: reason says, in plain words, why not.

// publicCatalogue.looksLikePlaceholder, exactly.
export const siteSaysPlaceholder = (text) => !text || /seenaryo|lorem ipsum|\[your company name\]|about us text/i.test(String(text));

const PLACEHOLDER_HIDDEN = "Hidden on the site: looks like placeholder text";

// FAQs page (/faqs): the API sends listed (is_main) active questions; the site
// keeps those with a question and an answer that don't look like placeholders.
//
// `state` is the one phrase a row shows beside its switch: "On the site",
// "Off", or "Blocked: …" when the switch is on but the site still hides it
// (a switch that is on never reads "Listed" while nothing shows).
// One "Live" switch sets both flags the API keeps (is_main for
// the Questions page, is_active for anywhere): `on` is the switch.
// `unblock`, when blocked: what to change so the website shows it.
export function faqOnSite(f) {
  const on = isOn(f.is_main) && Number(f.is_active) !== 0;
  if (!isOn(f.is_main)) return { shown: false, on: false, state: "Off", reason: "Not on the website" };
  if (Number(f.is_active) === 0 || Number(f.is_deleted) === 1) return { shown: false, on: false, state: "Off", reason: "Not on the website" };
  if (!String(f.questions || "").trim() || !String(f.answers || "").trim())
    return {
      shown: false,
      on,
      state: "Blocked: empty answer",
      reason: "Hidden on the website: the question or answer is empty",
      unblock: "Write the question and the answer, and it shows on the website.",
    };
  if (siteSaysPlaceholder(f.questions) || siteSaysPlaceholder(f.answers))
    return { shown: false, on, state: "Blocked: placeholder text", reason: PLACEHOLDER_HIDDEN, unblock: "Replace the placeholder words, and it shows on the website." };
  return { shown: true, on: true, state: "Live", reason: "" };
}

// A time or a price in an FAQ answer ("between 24h and 48h", "$25"): each
// product has its own, so a general answer goes wrong for most visas.
const STATES_TIME = /\b\d+\s*(?:h|hrs?|hours?|days?|working days|weeks?|months?)\b/i;
const STATES_PRICE = /[$€£]\s*\d|\b\d+\s*(?:usd|eur|dollars?|euros?)\b/i;

/**
 * Writing checks for an FAQ beyond filler text: spelling and grammar (the
 * house style checks) and a time or price that products set for
 * themselves. Plain sentences, as the Check column shows them.
 */
/**
 * The FAQ editor's notes, each with its plain replacement when it is one
 * (a grammar slip, a US spelling, a straight apostrophe, a doubled word):
 * [{ key, text, fix?: { field: "questions" | "answers", value } }] for a
 * one-click "Use this" (hala-33).
 */
export function faqSuggestions(question, answer) {
  const out = [];
  [
    ["questions", "Question", question],
    ["answers", "Answer", answer],
  ].forEach(([field, where, value]) =>
    styleIssues(value, { kind: "text" }).forEach((issue) =>
      out.push({ key: `${field}-${issue.key}`, text: `${where}: ${issue.text}`, fix: issue.fix ? { field, value: issue.fix } : undefined })
    )
  );
  const text = `${question || ""} ${answer || ""}`;
  if (STATES_TIME.test(text) || STATES_PRICE.test(text)) {
    out.push({ key: "time-price", text: "Gives a time or a price. Each product has its own: check this matches them, or point to the visa’s page instead." });
  }
  return out;
}

export function faqWritingWarnings(question, answer) {
  const out = [];
  const say = (where, issue) => `${where}: ${issue.text}`;
  styleIssues(question, { kind: "text" }).forEach((issue) => out.push(say("Question", issue)));
  styleIssues(answer, { kind: "text" }).forEach((issue) => out.push(say("Answer", issue)));
  const text = `${question || ""} ${answer || ""}`;
  if (STATES_TIME.test(text) || STATES_PRICE.test(text)) {
    out.push("Gives a time or a price. Each product has its own: check this matches them, or point to the visa’s page instead.");
  }
  return out;
}

// Testimonials: the site's section takes is_active === 1 with words, but no
// page shows that section yet.
export const TESTIMONIALS_MOUNTED = false;
export function testimonialReady(t) {
  if (Number(t.is_active) !== 1) return { shown: false, reason: "Switched off" };
  if (!String(t.description || "").trim()) return { shown: false, reason: "No words" };
  return { shown: TESTIMONIALS_MOUNTED, reason: TESTIMONIALS_MOUNTED ? "" : "Ready, but no website page shows testimonials yet" };
}

// "Trusted by" on the About page: every active logo with an image.
export function logoOnSite(l) {
  if (!l.image) return { shown: false, reason: "No image" };
  if (Number(l.is_active) === 0 || Number(l.is_deleted) === 1) return { shown: false, reason: "Switched off" };
  return { shown: true, reason: "" };
}

// Contact: the site uses ONE active record, the one marked main, or else the
// first active one. Its emails and phones (all three) show on the Contact
// page, the first ones in the footer, and its map on the Contact page.
export function contactInUse(rows) {
  const active = (rows || []).filter((c) => Number(c.is_active) === 1 && Number(c.is_deleted) !== 1).sort((a, b) => a.id - b.id);
  return active.find((c) => Number(c.is_main) === 1) || active[0] || null;
}

// Feature cards and working-process steps: no page of the current website
// reads them (the home page's "How it works" is written in the website code).
export const SERVICES_SHOWN = false;
export const STEPS_SHOWN = false;

export const shortName = (text, max = 40) => {
  const t = String(text || "")
    .trim()
    .replace(/\s+/g, " ");
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};
